import { useCallback, useEffect, useRef, useState } from 'react';

interface UseJobPollingOptions<T> {
  pollJob: (jobId: string) => Promise<T>;
  onUpdate: (job: T) => void;
  isTerminal: (job: T) => boolean;
  onTerminal?: (job: T) => void;
  intervalMs?: number;
}

export function useJobPolling<T>({
  pollJob,
  onUpdate,
  isTerminal,
  onTerminal,
  intervalMs = 2000,
}: UseJobPollingOptions<T>) {
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inFlightRef = useRef(false);
  const callbacksRef = useRef({ pollJob, onUpdate, isTerminal, onTerminal });
  const [pollFailures, setPollFailures] = useState(0);

  useEffect(() => {
    callbacksRef.current = { pollJob, onUpdate, isTerminal, onTerminal };
  }, [pollJob, onUpdate, isTerminal, onTerminal]);

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const pollOnce = useCallback(async (jobId: string) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const updated = await callbacksRef.current.pollJob(jobId);
      setPollFailures(0);
      callbacksRef.current.onUpdate(updated);
      if (callbacksRef.current.isTerminal(updated)) {
        stopPolling();
        callbacksRef.current.onTerminal?.(updated);
      }
    } catch {
      setPollFailures((count) => count + 1);
    } finally {
      inFlightRef.current = false;
    }
  }, [stopPolling]);

  // jobId is optional for pollers whose pollJob closes over its target
  // (e.g. a singleton status endpoint) rather than taking an id.
  const startPolling = useCallback((jobId = '') => {
    stopPolling();
    timerRef.current = setInterval(() => void pollOnce(jobId), intervalMs);
  }, [intervalMs, pollOnce, stopPolling]);

  const resetPollFailures = useCallback(() => setPollFailures(0), []);

  useEffect(() => stopPolling, [stopPolling]);

  return { pollFailures, pollOnce, resetPollFailures, startPolling, stopPolling };
}
